import { Alert, Button, Typography } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getMyCohortStatus } from '../api/mfa.api';

const DISMISS_KEY = 'mfaCohortBannerDismissedUntil';

// Pre-switch-on warning shown inside the app.
//
// The email is the primary channel, but it is one manual send: a member who
// misses it, bins it, or joined the cohort after it went out currently finds
// out when their login stops working. This closes that gap for anyone who is
// still signing in normally before the deadline.
//
// Only renders for a user who is in a cohort AND has not enrolled — the
// endpoint returns {cohort: null} for everyone else, so there is nothing to
// hide client-side.
export const CohortDeadlineBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissedAt, setDismissedAt] = useState<string | null>(() =>
    window.localStorage.getItem(DISMISS_KEY)
  );

  const { data } = useQuery('mfa-my-cohort', getMyCohortStatus, {
    // Cheap, and the answer only changes at a day boundary or on enrolment.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!data || data.cohort === null) return null;

  // Dismissal lasts for the calendar day only. The deadline is not going
  // away, and silencing it permanently would recreate the problem this
  // banner exists to solve.
  const today = new Date().toDateString();
  if (dismissedAt === today && !data.alreadyActive) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, today);
    setDismissedAt(today);
  };

  const urgent = data.alreadyActive || data.daysRemaining <= 3;

  return (
    <Alert
      color={urgent ? 'danger' : 'warning'}
      variant="soft"
      sx={{ borderRadius: 0, justifyContent: 'space-between', gap: 2 }}
      endDecorator={
        <>
          <Button
            size="sm"
            variant="solid"
            color={urgent ? 'danger' : 'warning'}
            onClick={() => navigate('/personalProfile')}
          >
            {t('mfa.banner_action')}
          </Button>
          {/* No dismiss once the deadline has passed — at that point the next
              login is blocked, so hiding the notice helps nobody. */}
          {!data.alreadyActive && (
            <Button size="sm" variant="plain" onClick={dismiss}>
              {t('mfa.banner_dismiss')}
            </Button>
          )}
        </>
      }
    >
      <Typography level="body-sm">
        {data.alreadyActive
          ? t('mfa.banner_active')
          : t('mfa.banner_countdown', { count: data.daysRemaining })}
      </Typography>
    </Alert>
  );
};

export default CohortDeadlineBanner;
